import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  IconButton,
  Paper,
  Alert,
  CircularProgress,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Avatar,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  Search,
  FilterList,
  EmojiEvents,
  Star,
  School,
  Psychology,
  Science,
  Calculate,
  Language,
  Brush,
  MusicNote,
  SportsEsports,
  Share,
  Download,
  Print,
  Celebration,
  TrendingUp,
  Timer,
  Speed,
  Group,
  Lightbulb,
  Favorite,
  Facebook,
  WhatsApp,
  Twitter,
  LinkedIn,
  ContentCopy,
  Email,
} from '@mui/icons-material';

interface Child {
  id: number;
  name: string;
  avatar: string;
  grade: string;
}

interface Achievement {
  id: number;
  childId: number;
  childName: string;
  title: string;
  description: string;
  category: 'academic' | 'behavioral' | 'participation' | 'creativity' | 'leadership' | 'collaboration';
  type: 'badge' | 'certificate' | 'medal' | 'trophy';
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  icon: string;
  earnedDate: Date;
  points: number;
  subject?: string;
  criteria: string;
  progress?: number;
  nextLevel?: {
    title: string;
    requirement: string;
    progress: number;
  };
  shareableUrl?: string;
}

interface AchievementStats {
  childId: number;
  childName: string;
  totalAchievements: number;
  totalPoints: number;
  byCategory: { [category: string]: number };
  byRarity: { [rarity: string]: number };
  recentStreak: number;
  rank: number;
  totalStudents: number;
}

const ParentAchievements: React.FC = () => {
  const navigate = useNavigate();
  const [children, setChildren] = useState<Child[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [stats, setStats] = useState<AchievementStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChild, setSelectedChild] = useState<number | 'all'>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedRarity, setSelectedRarity] = useState<string>('all');
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showImageInstructions, setShowImageInstructions] = useState(false);
  const [currentPlatform, setCurrentPlatform] = useState<string>('');
  const [currentShareText, setCurrentShareText] = useState<string>('');

  useEffect(() => {
    // Mock data - replace with API calls
    setLoading(true);
    setTimeout(() => {
      const mockChildren: Child[] = [
        { id: 1, name: 'Emma Johnson', avatar: 'E', grade: '6th Grade' },
        { id: 2, name: 'Lucas Johnson', avatar: 'L', grade: '3rd Grade' },
      ];

      const mockAchievements: Achievement[] = [
        {
          id: 1,
          childId: 1,
          childName: 'Emma Johnson',
          title: 'Math Whiz',
          description: 'Solved 100 consecutive math problems correctly',
          category: 'academic',
          type: 'badge',
          rarity: 'rare',
          icon: '🧮',
          earnedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
          points: 150,
          subject: 'Mathematics',
          criteria: 'Complete 100 math problems with 100% accuracy',
          shareableUrl: 'https://example.com/achievement/1'
        },
        {
          id: 2,
          childId: 1,
          childName: 'Emma Johnson',
          title: 'Consistent Learner',
          description: 'Maintained a 7-day learning streak',
          category: 'behavioral',
          type: 'medal',
          rarity: 'common',
          icon: '🔥',
          earnedDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
          points: 50,
          criteria: 'Study for 7 consecutive days',
          nextLevel: {
            title: 'Dedicated Scholar',
            requirement: 'Study for 14 consecutive days',
            progress: 50
          }
        },
        {
          id: 3,
          childId: 1,
          childName: 'Emma Johnson',
          title: 'Science Explorer',
          description: 'Completed first science course with excellence',
          category: 'academic',
          type: 'certificate',
          rarity: 'epic',
          icon: '🔬',
          earnedDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
          points: 250,
          subject: 'Science',
          criteria: 'Complete a science course with 95%+ average score'
        },
        {
          id: 4,
          childId: 1,
          childName: 'Emma Johnson',
          title: 'Problem Solver',
          description: 'Used creative thinking to solve complex problems',
          category: 'creativity',
          type: 'trophy',
          rarity: 'legendary',
          icon: '💡',
          earnedDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
          points: 500,
          criteria: 'Solve 5 advanced problems using alternative methods'
        },
        {
          id: 5,
          childId: 2,
          childName: 'Lucas Johnson',
          title: 'Code Creator',
          description: 'Built first programming project successfully',
          category: 'creativity',
          type: 'badge',
          rarity: 'rare',
          icon: '💻',
          earnedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
          points: 200,
          subject: 'Programming',
          criteria: 'Complete and submit a working programming project'
        },
        {
          id: 6,
          childId: 2,
          childName: 'Lucas Johnson',
          title: 'Team Player',
          description: 'Excellent collaboration in group activities',
          category: 'collaboration',
          type: 'medal',
          rarity: 'common',
          icon: '🤝',
          earnedDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
          points: 75,
          criteria: 'Participate actively in 3 group projects'
        },
        {
          id: 7,
          childId: 2,
          childName: 'Lucas Johnson',
          title: 'Quick Learner',
          description: 'Mastered new concepts faster than expected',
          category: 'academic',
          type: 'badge',
          rarity: 'rare',
          icon: '⚡',
          earnedDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
          points: 120,
          criteria: 'Complete 3 lessons ahead of schedule'
        }
      ];

      const mockStats: AchievementStats[] = [
        {
          childId: 1,
          childName: 'Emma Johnson',
          totalAchievements: 4,
          totalPoints: 950,
          byCategory: {
            academic: 2,
            behavioral: 1,
            creativity: 1,
            participation: 0,
            leadership: 0,
            collaboration: 0
          },
          byRarity: {
            common: 1,
            rare: 1,
            epic: 1,
            legendary: 1
          },
          recentStreak: 7,
          rank: 3,
          totalStudents: 150
        },
        {
          childId: 2,
          childName: 'Lucas Johnson',
          totalAchievements: 3,
          totalPoints: 395,
          byCategory: {
            academic: 1,
            behavioral: 0,
            creativity: 1,
            participation: 0,
            leadership: 0,
            collaboration: 1
          },
          byRarity: {
            common: 1,
            rare: 2,
            epic: 0,
            legendary: 0
          },
          recentStreak: 4,
          rank: 8,
          totalStudents: 80
        }
      ];

      setChildren(mockChildren);
      setAchievements(mockAchievements);
      setStats(mockStats);
      setLoading(false);
    }, 1000);
  }, []);

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'legendary':
        return 'warning';
      case 'epic':
        return 'secondary';
      case 'rare':
        return 'primary';
      case 'common':
        return 'success';
      default:
        return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'badge':
        return <EmojiEvents />;
      case 'certificate':
        return <School />;
      case 'medal':
        return <Star />;
      case 'trophy':
        return <EmojiEvents />;
      default:
        return <EmojiEvents />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'academic':
        return <School color="primary" />;
      case 'behavioral':
        return <Favorite color="secondary" />;
      case 'participation':
        return <Group color="info" />;
      case 'creativity':
        return <Lightbulb color="warning" />;
      case 'leadership':
        return <TrendingUp color="success" />;
      case 'collaboration':
        return <Group color="info" />;
      default:
        return <Star />;
    }
  };

  const filteredAchievements = achievements.filter(achievement => {
    const matchesSearch = achievement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         achievement.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         achievement.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesChild = selectedChild === 'all' || achievement.childId === selectedChild;
    const matchesCategory = selectedCategory === 'all' || achievement.category === selectedCategory;
    const matchesRarity = selectedRarity === 'all' || achievement.rarity === selectedRarity;

    return matchesSearch && matchesChild && matchesCategory && matchesRarity;
  });

  const categories = Array.from(new Set(achievements.map(achievement => achievement.category)));
  const rarities = Array.from(new Set(achievements.map(achievement => achievement.rarity)));

  const handleAchievementClick = (achievement: Achievement) => {
    setSelectedAchievement(achievement);
  };

  const handleShare = (achievement: Achievement) => {
    setSelectedAchievement(achievement);
    setShowShareDialog(true);
  };

  const generateCertificateImage = (achievement: Achievement) => {
    // Create a canvas for the certificate
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    // Certificate dimensions (optimized for social media)
    canvas.width = 800;
    canvas.height = 600;

    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#667eea');
    gradient.addColorStop(1, '#764ba2');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add decorative border
    ctx.strokeStyle = '#FFD700';
    ctx.lineWidth = 8;
    ctx.strokeRect(20, 20, canvas.width - 40, canvas.height - 40);

    // Inner border
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2;
    ctx.strokeRect(35, 35, canvas.width - 70, canvas.height - 70);

    // Title
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 48px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('🎉 ACHIEVEMENT EARNED 🎉', canvas.width / 2, 120);

    // Achievement icon (large)
    ctx.font = '120px Arial';
    ctx.fillText(achievement.icon, canvas.width / 2, 220);

    // Achievement title
    ctx.font = 'bold 36px Arial';
    ctx.fillStyle = '#FFD700';
    ctx.fillText(achievement.title, canvas.width / 2, 280);

    // Child name
    ctx.font = 'bold 32px Arial';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(`Congratulations ${achievement.childName}!`, canvas.width / 2, 330);

    // Achievement description (wrapped text)
    ctx.font = '24px Arial';
    ctx.fillStyle = '#E0E0E0';
    const words = achievement.description.split(' ');
    let line = '';
    let y = 380;
    
    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + ' ';
      const metrics = ctx.measureText(testLine);
      if (metrics.width > canvas.width - 100 && i > 0) {
        ctx.fillText(line, canvas.width / 2, y);
        line = words[i] + ' ';
        y += 30;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, canvas.width / 2, y);

    // App branding
    ctx.font = 'bold 28px Arial';
    ctx.fillStyle = '#FFD700';
    ctx.fillText('🎓 EduProgress Learning Platform', canvas.width / 2, y + 60);

    // Date
    ctx.font = '20px Arial';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(`Earned on ${achievement.earnedDate.toLocaleDateString()}`, canvas.width / 2, y + 90);

    // Convert to data URL
    return canvas.toDataURL('image/png', 0.9);
  };

  const generateShareContent = (achievement: Achievement) => {
    const appName = "EduProgress";
    const appUrl = "https://eduProgress.com/join";
    
    // Platform-specific optimized content
    const platformContent = {
      // Twitter - Short and punchy with hashtags
      twitter: {
        text: `🎉 ${achievement.childName} just earned "${achievement.title}"! ${achievement.icon}\n\nSo proud! 💪 Track your child's progress with ${appName}:\n${appUrl}`,
        hashtags: ['ProudParent', 'EducationWins', 'LearningJourney', 'EduProgress']
      },
      
      // WhatsApp - Personal and conversational
      whatsapp: {
        text: `🎉 Great news! ${achievement.childName} just earned the "${achievement.title}" achievement! ${achievement.icon}\n\n"${achievement.description}"\n\nI'm so proud! 💪\n\nWe've been using EduProgress to track learning progress and it's amazing how engaged kids become when achievements are celebrated!\n\n👉 Check it out: ${appUrl}\n\nYour kids might love it too! 😊`
      },
      
      // Facebook - Detailed and engaging
      facebook: {
        text: `🎉 Amazing milestone! My child ${achievement.childName} just earned the "${achievement.title}" achievement! ${achievement.icon}\n\n"${achievement.description}"\n\nI'm incredibly proud of their dedication and hard work! 💪\n\n📚 We've been using ${appName} to track learning progress and celebrate achievements. It's incredible how much more engaged and motivated kids become when their efforts are recognized and celebrated!\n\n👨‍👩‍👧‍👦 Other parents - if you're looking for ways to motivate your children's learning journey, check this out: ${appUrl}\n\n#EducationMatters #ProudParent #LearningSuccess #StudentAchievement #${appName}`
      },
      
      // LinkedIn - Professional and educational focus
      linkedin: {
        text: `🎓 Celebrating educational milestones! My child ${achievement.childName} achieved "${achievement.title}" through ${appName}.\n\n"${achievement.description}"\n\nAs parents, finding tools that genuinely engage our children in learning is invaluable. The achievement system has transformed how my child approaches education - turning learning into an exciting journey rather than a chore.\n\n👨‍👩‍👧‍👦 Fellow parents and educators: ${appUrl}\n\n#EducationalTechnology #ParentingWins #LearningMotivation #StudentSuccess`
      }
    };
    
    return {
      title: `${achievement.childName} earned: ${achievement.title}!`,
      platformContent,
      certificateImage: generateCertificateImage(achievement),
      url: appUrl
    };
  };

  const handleSocialShare = async (platform: string, achievement: Achievement) => {
    const content = generateShareContent(achievement);
    const platformText = content.platformContent[platform as keyof typeof content.platformContent]?.text || '';

    // Check if Web Share API is available and supports files (mobile devices)
    if (navigator.share && platform === 'whatsapp') {
      try {
        // Convert certificate image to blob
        const response = await fetch(content.certificateImage);
        const blob = await response.blob();
        const file = new File([blob], `${achievement.childName}-Achievement.png`, { type: 'image/png' });

        // Use Web Share API with image
        await navigator.share({
          title: content.title,
          text: platformText,
          files: [file]
        });
        return;
      } catch (error) {
        console.log('Web Share API not available or failed, falling back to URL sharing');
      }
    }

    // For platforms that don't support image sharing via URL, we'll copy the image to clipboard
    // and provide instructions
    if (platform === 'whatsapp' || platform === 'twitter') {
      try {
        // Copy image to clipboard
        const response = await fetch(content.certificateImage);
        const blob = await response.blob();
        await navigator.clipboard.write([
          new ClipboardItem({
            'image/png': blob
          })
        ]);

        // Show instructions modal
        setShowImageInstructions(true);
        setCurrentPlatform(platform);
        setCurrentShareText(platformText);
        return;
      } catch (error) {
        console.log('Clipboard API not available, proceeding with text-only share');
      }
    }

    // Fallback to text-only sharing
    let shareUrl = '';
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(content.url)}&quote=${encodeURIComponent(platformText)}`;
        break;
      case 'twitter':
        const twitterContent = content.platformContent.twitter;
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterContent.text)}&hashtags=${twitterContent.hashtags.join(',')}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(platformText)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(content.url)}&title=${encodeURIComponent(content.title)}&summary=${encodeURIComponent(platformText)}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const handleDownloadCertificate = (achievement: Achievement) => {
    const content = generateShareContent(achievement);
    const link = document.createElement('a');
    link.download = `${achievement.childName}-${achievement.title}-Certificate.png`;
    link.href = content.certificateImage;
    link.click();
  };

  const handleCopyLink = (achievement: Achievement) => {
    const content = generateShareContent(achievement);
    const platformText = content.platformContent.whatsapp.text; // Use WhatsApp text as default
    navigator.clipboard.writeText(platformText).then(() => {
      alert('Share text copied to clipboard!');
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate('/parent-dashboard')}
            sx={{ cursor: 'pointer' }}
          >
            Parent Dashboard
          </Link>
          <Typography color="text.primary">Achievements</Typography>
        </Breadcrumbs>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/parent-dashboard')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Achievements & Awards
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Celebrate your children's learning milestones and accomplishments
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Download />}>
            Export
          </Button>
          <Button variant="outlined" startIcon={<Print />}>
            Print Certificates
          </Button>
        </Box>
      </Box>


      {/* Filters */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search achievements..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Child</InputLabel>
              <Select
                value={selectedChild}
                label="Child"
                onChange={(e) => setSelectedChild(e.target.value as number | 'all')}
              >
                <MenuItem value="all">All Children</MenuItem>
                {children.map((child) => (
                  <MenuItem key={child.id} value={child.id}>
                    {child.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Rarity</InputLabel>
              <Select
                value={selectedRarity}
                label="Rarity"
                onChange={(e) => setSelectedRarity(e.target.value)}
              >
                <MenuItem value="all">All Rarities</MenuItem>
                {rarities.map((rarity) => (
                  <MenuItem key={rarity} value={rarity}>
                    {rarity}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              fullWidth
              onClick={() => {
                setSearchTerm('');
                setSelectedChild('all');
                setSelectedCategory('all');
                setSelectedRarity('all');
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Child-Specific Achievement Blocks */}
      {children.filter(child => selectedChild === 'all' || child.id === selectedChild).map((child) => {
        const childAchievements = filteredAchievements.filter(achievement => achievement.childId === child.id);
        const childStats = stats.find(stat => stat.childId === child.id);
        
        if (childAchievements.length === 0) return null;
        
        return (
          <Card key={child.id} sx={{ mb: 4 }}>
            <CardContent>
              {/* Child Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                    {child.avatar}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {child.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {childStats?.totalAchievements} Achievements • {childStats?.totalPoints} Points
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  onClick={() => navigate(`/parent-child-dashboard/${child.id}`)}
                >
                  View Full Dashboard
                </Button>
              </Box>

              {/* Achievement Stats for this Child */}
              {childStats && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                        {childStats.totalAchievements}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Achievements
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="warning.main" sx={{ fontWeight: 600 }}>
                        {childStats.totalPoints}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Points
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                        {childStats.byRarity.legendary || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Legendary
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="info.main" sx={{ fontWeight: 600 }}>
                        {childStats.byRarity.epic || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Epic
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              )}

              {/* Achievement Grid for this Child */}
              <Grid container spacing={3}>
                {childAchievements.map((achievement) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={achievement.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => handleAchievementClick(achievement)}
              >
                <CardContent sx={{ textAlign: 'center', pb: 1 }}>
                  <Box sx={{ position: 'relative', mb: 2 }}>
                    <Typography variant="h2" sx={{ mb: 1 }}>
                      {achievement.icon}
                    </Typography>
                    <Badge 
                      badgeContent={getTypeIcon(achievement.type)} 
                      color="primary"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: '25%',
                        '& .MuiBadge-badge': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          padding: '4px'
                        }
                      }}
                    />
                  </Box>
                  
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {achievement.title}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {achievement.description}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
                    <Chip 
                      label={achievement.category} 
                      icon={getCategoryIcon(achievement.category)}
                      size="small" 
                    />
                    <Chip 
                      label={achievement.rarity} 
                      color={getRarityColor(achievement.rarity) as any}
                      size="small" 
                    />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {achievement.childName}
                    </Typography>
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                      +{achievement.points} pts
                    </Typography>
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    Earned {achievement.earnedDate.toLocaleDateString()}
                  </Typography>

                  {achievement.nextLevel && (
                    <Box sx={{ mt: 2, p: 1, bgcolor: 'info.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="info.main" sx={{ fontWeight: 600 }}>
                        Next: {achievement.nextLevel.title}
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={achievement.nextLevel.progress} 
                        sx={{ mt: 0.5, height: 4 }}
                      />
                    </Box>
                  )}
                </CardContent>
                
                <Box sx={{ p: 1, pt: 0 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Share />}
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShare(achievement);
                    }}
                    sx={{ 
                      background: 'linear-gradient(45deg, #FF6B6B 30%, #4ECDC4 90%)',
                      color: 'white',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'linear-gradient(45deg, #FF5252 30%, #26A69A 90%)',
                        transform: 'translateY(-1px)',
                        boxShadow: 3
                      }
                    }}
                  >
                    Share Achievement
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
              </Grid>
            </CardContent>
          </Card>
        );
      })}

      {/* No Data Message */}
      {children.filter(child => selectedChild === 'all' || child.id === selectedChild).every(child => {
        const childAchievements = filteredAchievements.filter(achievement => achievement.childId === child.id);
        return childAchievements.length === 0;
      }) && (
        <Alert severity="info" sx={{ textAlign: 'center' }}>
          No achievements found matching your criteria.
        </Alert>
      )}

      {/* Achievement Detail Dialog */}
      <Dialog
        open={Boolean(selectedAchievement)}
        onClose={() => setSelectedAchievement(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedAchievement && (
          <>
            <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
              <Typography variant="h2" sx={{ mb: 1 }}>
                {selectedAchievement.icon}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {selectedAchievement.title}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {selectedAchievement.childName}
              </Typography>
            </DialogTitle>
            
            <DialogContent>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedAchievement.description}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
                  <Chip 
                    label={selectedAchievement.category} 
                    icon={getCategoryIcon(selectedAchievement.category)}
                  />
                  <Chip 
                    label={selectedAchievement.rarity} 
                    color={getRarityColor(selectedAchievement.rarity) as any}
                  />
                  <Chip 
                    label={selectedAchievement.type}
                    icon={getTypeIcon(selectedAchievement.type)}
                  />
                </Box>
              </Box>

              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Achievement Criteria:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedAchievement.criteria}
                </Typography>
              </Paper>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Earned: {selectedAchievement.earnedDate.toLocaleDateString()}
                </Typography>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                  +{selectedAchievement.points} Points
                </Typography>
              </Box>

              {selectedAchievement.subject && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Subject: {selectedAchievement.subject}
                  </Typography>
                </Box>
              )}

              {selectedAchievement.nextLevel && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="info.main" gutterBottom>
                    Next Level: {selectedAchievement.nextLevel.title}
                  </Typography>
                  <Typography variant="body2" color="info.dark" sx={{ mb: 1 }}>
                    {selectedAchievement.nextLevel.requirement}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={selectedAchievement.nextLevel.progress} 
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                  <Typography variant="caption" color="info.dark">
                    {selectedAchievement.nextLevel.progress}% Complete
                  </Typography>
                </Box>
              )}
            </DialogContent>
            
            <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
              <Button onClick={() => setSelectedAchievement(null)}>
                Close
              </Button>
              <Button 
                variant="contained" 
                startIcon={<Share />}
                onClick={() => handleShare(selectedAchievement)}
              >
                Share Achievement
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Enhanced Share Dialog */}
      <Dialog
        open={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Share color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Share This Amazing Achievement!
            </Typography>
          </Box>
          {selectedAchievement && (
            <Typography variant="subtitle1" color="text.secondary">
              Help other parents discover EduProgress
            </Typography>
          )}
        </DialogTitle>
        
        <DialogContent>
          {selectedAchievement && (
            <Box>
              {/* Achievement Preview */}
              <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50', textAlign: 'center' }}>
                <Typography variant="h2" sx={{ mb: 1 }}>
                  {selectedAchievement.icon}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {selectedAchievement.childName} earned: {selectedAchievement.title}!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedAchievement.description}
                </Typography>
              </Paper>

              {/* Certificate Image Preview */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  🏆 Certificate Image:
                </Typography>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                  <img 
                    src={generateShareContent(selectedAchievement).certificateImage} 
                    alt="Achievement Certificate"
                    style={{ 
                      maxWidth: '100%', 
                      height: 'auto', 
                      borderRadius: '8px',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Box sx={{ mt: 2 }}>
                    <Button 
                      variant="outlined" 
                      startIcon={<Download />}
                      onClick={() => handleDownloadCertificate(selectedAchievement)}
                      size="small"
                    >
                      Download Certificate
                    </Button>
                  </Box>
                </Paper>
              </Box>

              {/* Platform-Specific Text Previews */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  📱 Platform-Optimized Text:
                </Typography>
                
                {/* Twitter Preview */}
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#E3F2FD', border: '1px solid #1DA1F2' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Twitter sx={{ color: '#1DA1F2', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ color: '#1DA1F2', fontWeight: 600 }}>
                      Twitter (Character Optimized)
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.4 }}>
                    {generateShareContent(selectedAchievement).platformContent.twitter.text}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    #{generateShareContent(selectedAchievement).platformContent.twitter.hashtags.join(' #')}
                  </Typography>
                </Paper>

                {/* WhatsApp Preview */}
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#E8F5E8', border: '1px solid #25D366' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <WhatsApp sx={{ color: '#25D366', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ color: '#25D366', fontWeight: 600 }}>
                      WhatsApp (Personal & Engaging)
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.4 }}>
                    {generateShareContent(selectedAchievement).platformContent.whatsapp.text}
                  </Typography>
                </Paper>
              </Box>

              {/* Social Media Buttons */}
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                🚀 Share on Social Media:
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Facebook />}
                    onClick={() => handleSocialShare('facebook', selectedAchievement)}
                    sx={{ 
                      bgcolor: '#1877F2', 
                      color: 'white',
                      '&:hover': { bgcolor: '#166FE5' },
                      py: 1.5
                    }}
                  >
                    Facebook
                  </Button>
                </Grid>
                
                <Grid item xs={6} sm={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Twitter />}
                    onClick={() => handleSocialShare('twitter', selectedAchievement)}
                    sx={{ 
                      bgcolor: '#1DA1F2', 
                      color: 'white',
                      '&:hover': { bgcolor: '#1A91DA' },
                      py: 1.5
                    }}
                  >
                    Twitter
                  </Button>
                </Grid>
                
                <Grid item xs={6} sm={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<WhatsApp />}
                    onClick={() => handleSocialShare('whatsapp', selectedAchievement)}
                    sx={{ 
                      bgcolor: '#25D366', 
                      color: 'white',
                      '&:hover': { bgcolor: '#22C55E' },
                      py: 1.5
                    }}
                  >
                    WhatsApp
                  </Button>
                </Grid>
                
                <Grid item xs={6} sm={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<LinkedIn />}
                    onClick={() => handleSocialShare('linkedin', selectedAchievement)}
                    sx={{ 
                      bgcolor: '#0A66C2', 
                      color: 'white',
                      '&:hover': { bgcolor: '#0958A5' },
                      py: 1.5
                    }}
                  >
                    LinkedIn
                  </Button>
                </Grid>
              </Grid>

              {/* Additional Options */}
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                📋 More Options:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="outlined" 
                  startIcon={<ContentCopy />}
                  onClick={() => handleCopyLink(selectedAchievement)}
                  sx={{ flex: 1, minWidth: 120 }}
                >
                  Copy Text
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<Email />}
                  onClick={() => {
                    const content = generateShareContent(selectedAchievement);
                    window.location.href = `mailto:?subject=${encodeURIComponent(content.title)}&body=${encodeURIComponent(content.text)}`;
                  }}
                  sx={{ flex: 1, minWidth: 120 }}
                >
                  Email
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<Download />}
                  onClick={() => handleDownloadCertificate(selectedAchievement)}
                  sx={{ flex: 1, minWidth: 120 }}
                >
                  Download Certificate
                </Button>
              </Box>

              {/* Marketing Message */}
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  🎯 Help us grow our community!
                </Typography>
                <Typography variant="body2">
                  When you share achievements, you're helping other parents discover how EduProgress can transform their child's learning journey. Every share helps build a stronger educational community!
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button onClick={() => setShowShareDialog(false)} size="large">
            Close
          </Button>
          <Button 
            variant="contained" 
            size="large"
            startIcon={<Celebration />}
            onClick={() => {
              if (selectedAchievement) {
                handleSocialShare('facebook', selectedAchievement);
              }
            }}
            sx={{
              background: 'linear-gradient(45deg, #FF6B6B 30%, #4ECDC4 90%)',
              color: 'white',
              fontWeight: 600,
              px: 3
            }}
          >
            Share Now!
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Sharing Instructions Modal */}
      <Dialog
        open={showImageInstructions}
        onClose={() => setShowImageInstructions(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Share color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Share with Certificate Image
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              📋 Certificate image copied to clipboard!
            </Typography>
            <Typography variant="body2">
              Follow these steps to share with the certificate image:
            </Typography>
          </Alert>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              📱 {currentPlatform === 'whatsapp' ? 'WhatsApp' : 'Twitter'} Sharing Steps:
            </Typography>
            
            {currentPlatform === 'whatsapp' ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Step 1:</strong> Open WhatsApp and select the chat or group
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Step 2:</strong> Tap the attachment button (📎) and select "Photos & Videos"
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Step 3:</strong> Paste the certificate image (long press and paste)
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Step 4:</strong> Add the text below as caption
                </Typography>
                <Typography variant="body2">
                  <strong>Step 5:</strong> Send the message with image!
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Step 1:</strong> Open Twitter and click "Tweet"
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Step 2:</strong> Paste the certificate image (Ctrl+V or Cmd+V)
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Step 3:</strong> Add the text below to your tweet
                </Typography>
                <Typography variant="body2">
                  <strong>Step 4:</strong> Tweet with the certificate image!
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              📝 Text to copy:
            </Typography>
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {currentShareText}
              </Typography>
            </Paper>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ContentCopy />}
                onClick={() => {
                  navigator.clipboard.writeText(currentShareText);
                  alert('Text copied to clipboard!');
                }}
                fullWidth
              >
                Copy Text
              </Button>
            </Box>
          </Box>

          <Alert severity="success">
            <Typography variant="body2">
              💡 <strong>Pro Tip:</strong> The certificate image makes your post much more eye-catching and likely to be shared by others!
            </Typography>
          </Alert>
        </DialogContent>
        
        <DialogActions sx={{ justifyContent: 'center', p: 2 }}>
          <Button onClick={() => setShowImageInstructions(false)} size="large">
            Got it!
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setShowImageInstructions(false);
              if (currentPlatform === 'whatsapp') {
                window.open('https://web.whatsapp.com/', '_blank');
              } else if (currentPlatform === 'twitter') {
                window.open('https://twitter.com/compose/tweet', '_blank');
              }
            }}
            size="large"
          >
            Open {currentPlatform === 'whatsapp' ? 'WhatsApp' : 'Twitter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ParentAchievements;
